import { Formik } from 'formik';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Button, FormField, PredefinedCards, Skeleton, ToggleButton } from '@components';
import { SCREEN_BREAKPOINTS } from '@constants';
import {
  cacheLevelType,
  cacheTTLOptionType,
  columnDirection,
  Dictionaries,
  FormType,
  InputMod,
  InputType,
  StatusType,
  Translation,
} from '@enums';
import { useAppDispatch, useErrorFormInner, useMediaQuery } from '@hooks';
import {
  addSnack,
  resetSubmitFormState,
  submitFormRequest,
  useFormContent,
  useFormError,
  useFormLoadingStatus,
  useFormSaveStatus,
  useFormSubmissionStatus,
} from '@store';
import { getOptionListFromCatalog } from '@utils';

import { CacheFileExtensionModal } from '../subComponents/modals/CacheFileExtensionModal';

interface PredefinedCacheViewProps {
  onFormikInstance: (formik) => void;
  formikRef: any;
}

export const PredefinedCacheView = ({ formikRef, onFormikInstance }: PredefinedCacheViewProps) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnCache;

  const { t: tDescription } = useTranslation(Translation.Description, { keyPrefix: 'configuration.cache' });
  const { t: tToasts } = useTranslation(Translation.Configuration, { keyPrefix: 'toasts.predefinedCache' });
  const { t: tForm } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.cache' });
  const { t: tButton } = useTranslation(Translation.Configuration, { keyPrefix: 'buttons.cache' });

  const formList = useFormContent({ formType });
  const isSubmitting = useFormSubmissionStatus({ formType });
  const formLoading = useFormLoadingStatus({ formType });
  const saveSuccess = useFormSaveStatus(formType);
  const submitError = useFormError({ formType });

  const isAboveSm = useMediaQuery(`(min-width: ${SCREEN_BREAKPOINTS.sm}px)`);

  const [openConfigureExtensionModal, setOpenConfigureExtensionModal] = useState<boolean>(false);
  const [showCurrentToast, setShowCurrentToast] = useState<boolean>(false);
  const [currentDataKey, setCurrentDataKey] = useState<string | null>(null);

  const predefinedCacheInitialValues = {
    status: true,
    cache_mode: cacheLevelType.Standard,
    server_cache_ttl: cacheTTLOptionType.Follow_Origin_Cache_Header,
    browser_cache_ttl: cacheTTLOptionType.Follow_Origin_Cache_Header,
    overwrite_cache_header: '0',
    extensions: [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'tiff',
      'webp',
      'ico',
      'svg',
      'js',
      'css',
      'woff',
      'woff2',
      'ttf',
      'tif',
      'tiff',
    ],
  };

  const initialValues = {
    status: formList?.status ?? predefinedCacheInitialValues.status,
    cache_mode: formList?.cache_mode ?? predefinedCacheInitialValues.cache_mode,
    server_cache_ttl: formList?.server_cache_ttl ?? predefinedCacheInitialValues.server_cache_ttl,
    browser_cache_ttl: formList?.browser_cache_ttl ?? predefinedCacheInitialValues.browser_cache_ttl,
    overwrite_cache_header: formList?.overwrite_cache_header ?? predefinedCacheInitialValues.overwrite_cache_header,
    extensions:
      formList?.extensions?.map((extension, index) => `${index !== 0 ? '\n' : ''}${extension}`) ??
      predefinedCacheInitialValues.extensions,
  };

  const handleSubmit = (values: any) => {
    const { extensions: oldExtensions, ...updatedValues } = values;
    const extensions = oldExtensions?.map((value) => value.replace(/\n/g, '')) ?? [];

    dispatch(
      submitFormRequest({
        formType,
        formData: { application_id, extensions, ...updatedValues },
        globalOptions: {
          returnResult: true,
          content: true,
        },
      }),
    );
  };

  const handleConfigure = () => {
    dispatch(resetSubmitFormState({ formType }));
    setOpenConfigureExtensionModal(!openConfigureExtensionModal);
  };

  const onOptionClick = (event, formik, name) => {
    const selectOptions = ['cache_mode', 'server_cache_ttl', 'browser_cache_ttl'];
    const currentOption = selectOptions.includes(name) ? event.target.value : event;
    formik.setFieldValue(name, currentOption);
    const cleanedOption = currentOption.map((value: string) => value.replace(/\n/g, ''));

    dispatch(
      submitFormRequest({
        formType,
        formData: { application_id, [name]: cleanedOption },
        globalOptions: {
          returnResult: true,
          content: true,
          toast: false,
        },
      }),
    );
    setShowCurrentToast(true);
    setCurrentDataKey(name);
  };

  useEffect(() => {
    if (!submitError && !isSubmitting && saveSuccess) {
      if (showCurrentToast && currentDataKey) {
        dispatch(addSnack({ type: StatusType.Success, message: tToasts(`${currentDataKey}Configured`) }));
        setShowCurrentToast(false);
        setCurrentDataKey(null);
      }
    }
  }, [application_id, currentDataKey, isSubmitting, saveSuccess, showCurrentToast, submitError]);

  useEffect(() => {
    if (formikRef.current?.errors && Object.keys(formikRef.current.errors).length > 0) {
      formikRef.current.validateForm();
    }
  }, [tForm]);

  if (formLoading) {
    return (
      <div className="space-y-4">
        {[...Array(7)].map((_, index) => (
          <Skeleton key={index} className="flex w-full rounded-2xl h-32" />
        ))}
      </div>
    );
  }
  const SecurityFormInner = () => {
    useErrorFormInner({ formType, isSubmitting });
    return null;
  };

  return (
    <Formik innerRef={formikRef} initialValues={initialValues} onSubmit={handleSubmit}>
      {(formik) => {
        if (onFormikInstance) onFormikInstance(formik);

        const handleModalResetAndClose = (modalSetter, openModal, fieldName) => {
          formik.setFieldValue(fieldName, initialValues[fieldName]);
          formik.setFieldError(fieldName, undefined);
          modalSetter(!openModal);
        };

        const handleConfigurationExtensionModal = () => {
          handleModalResetAndClose(setOpenConfigureExtensionModal, openConfigureExtensionModal, 'extensions');
        };

        return (
          <Fragment>
            <SecurityFormInner />
            <PredefinedCards
              besideTitle={!isAboveSm}
              title={tForm('enabled')}
              description={tForm('togglePredefinedCacheRules')}
            >
              <FormField
                withoutLabel
                label="Enabled"
                name="status"
                fullWidth
                smallFont
                direction={columnDirection.Row}
                inputType={InputType.Custom}
              >
                <ToggleButton disabled={isSubmitting} className="-ml-3" name="status" />
              </FormField>
            </PredefinedCards>
            <PredefinedCards
              title={tForm('cacheLevel')}
              description={tForm('determineHowMuchOfYourWebsitesStaticContentYouWant7BullToCache')}
            >
              <FormField
                label={tForm('cacheLevel')}
                name="cache_mode"
                inputType={InputType.Select}
                inputMod={InputMod.Filled}
                withoutLabel
                smallFont
                loading={isSubmitting}
                fullWidth
                disabled={!formik.values?.status || isSubmitting}
                placeholder={tForm('pleaseSelectYourCacheLevel')}
                options={getOptionListFromCatalog({ name: Dictionaries.CachingLevelOption })}
              />
            </PredefinedCards>
            <PredefinedCards
              title={tForm('cacheOverwriteHeader')}
              description={tForm(
                'specifyCacheOverwriteHeaderToControlCachingBehaviorEnsuringUpdatedContentIsServedPromptlyAndCachedVersionsAreBypassedAsNeeded',
              )}
            >
              <FormField
                label={tForm('cacheOverwriteHeader')}
                name="overwrite_cache_header"
                inputType={InputType.Select}
                inputMod={InputMod.Filled}
                withoutLabel
                smallFont
                fullWidth
                loading={isSubmitting}
                disabled={!formik.values?.status || isSubmitting}
                placeholder={tForm('pleaseSelectYourOverwriteHeader')}
                options={getOptionListFromCatalog({ name: Dictionaries.CacheOverwriteHedaer })}
              />
            </PredefinedCards>
            <PredefinedCards
              title={tForm('serverCacheTimeToLive')}
              description={tForm(
                'duringThisPeriodRequestsForFilesAreServedFromTheServerCacheReducingLoadOnTheOriginServerAndSpeedingUpResponses',
              )}
            >
              <FormField
                label={tForm('serverCacheTimeToLive')}
                name="server_cache_ttl"
                inputType={InputType.Select}
                inputMod={InputMod.Filled}
                withoutLabel
                smallFont
                fullWidth
                loading={isSubmitting}
                disabled={!formik.values?.status || isSubmitting}
                placeholder={tForm('pleaseSelectYourServerCache')}
                options={getOptionListFromCatalog({ name: Dictionaries.CacheTTLOption })}
              />
            </PredefinedCards>
            <PredefinedCards
              title={tForm('browserCacheTimeToLive')}
              description={tForm('determineTheLengthOfTime7BullInstructsAVisitorsBrowserToCacheFiles')}
            >
              <FormField
                label={tForm('browserCacheTimeToLive')}
                name="browser_cache_ttl"
                inputType={InputType.Select}
                inputMod={InputMod.Filled}
                withoutLabel
                smallFont
                loading={isSubmitting}
                fullWidth
                disabled={!formik.values?.status || isSubmitting}
                placeholder={tForm('pleaseSelectYourBrowserCache')}
                options={getOptionListFromCatalog({ name: Dictionaries.CacheTTLOption })}
              />
            </PredefinedCards>
            <PredefinedCards
              title={tForm('cacheFileExtension')}
              description={tForm('configureWhichFileTypes7BullShouldCacheByEnteringDifferentFileExtensions')}
            >
              <Button
                onClick={handleConfigure}
                fullWidth
                disabled={!formik.values?.status}
                loading={isSubmitting}
                className="flex"
              >
                {tButton('configure')}
              </Button>
            </PredefinedCards>
            {openConfigureExtensionModal && (
              <CacheFileExtensionModal
                open={openConfigureExtensionModal}
                title={tForm('configureCacheFileExtension')}
                placeholder={tForm('enterYourCacheRulesName')}
                description={tDescription('configureCacheFileExtension')}
                formik={formik}
                onSubmit={onOptionClick}
                onClose={handleConfigurationExtensionModal}
                formType={formType}
                name="extensions"
              />
            )}
          </Fragment>
        );
      }}
    </Formik>
  );
};
